import { HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import FileSaver from 'file-saver';
import moment from 'moment';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { BehaviorSubject, finalize, map, Subject, takeUntil } from 'rxjs';
import { AccountService } from 'src-private/app/areas/account/services/account.service';
import { DialogType } from 'src-private/app/enums/dialog-type';
import { ConfirmDialogComponent } from 'src-private/app/shared/confirm-dialog/confirm-dialog.component';
import { FrameworkComponent } from 'src-private/app/shared/framework/framework.component';
import { CellBuilder } from 'src-private/app/shared/table-adapter/cell-builder/cell-builder';
import { TableAdapterComponent } from 'src-private/app/shared/table-adapter/table-adapter.component';
import { IDocument, DocumentTypes } from '../../interfaces/document';
import { ClaimsService } from '../../services/claims.service';
import { DocumentAddDialogComponent } from '../document-add-dialog/document-add-dialog.component';
import { environment } from "src-private/environments/environment";
import { ActivatedRoute } from '@angular/router';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { CellActions } from '../../../../shared/table-adapter/cell-builder/cell-type';
import { DocumentConflictDialogComponent } from '../document-conflict-dialog/document-conflict-dialog.component';
import { ToastrService } from "ngx-toastr";
import { DocumentMoveDialogComponent } from '../document-move-dialog/document-move-dialog.component';
import { DocumentService } from '../../services/document.service';
import { WarrantyDocumentService } from '../../services/warranty-document.service';
import { ClaimsRetrieve } from '../../models/claims-retrieve.model';
import { BooleanLiteral } from 'typescript';

@Component({
  selector: 'app-warranty-documents',
  templateUrl: './warranty-documents.component.html',
  styleUrls: ['./warranty-documents.component.scss']
})
export class WarrantyDocumentsComponent extends FrameworkComponent implements OnInit, AfterViewInit{

  @Input() columns: CellBuilder[];
  @Input() warrantyId: number;
  @Input() customerId: number;

  public faPlus = faPlus;

  private claimId: number;
  public isAdjuster: boolean;
  public isAuditor: boolean;
  public isLoading = false;

  private ngUnsubscribe: Subject<any> = new Subject();
  @BlockUI() blockUI: NgBlockUI;

  @ViewChild("warrantyDocumentsTable")
  warrantyDocumentsTable: TableAdapterComponent<IDocument>;

  constructor(
    public dialog: MatDialog,
    private warrantyDocumentService: WarrantyDocumentService,
    private accountService: AccountService,
    private claimsService: ClaimsService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private documentService: DocumentService
  ) { 
    super();
    this.claimId = this.route.snapshot.data['claimId']
  }

  ngOnInit(): void {
    this.isAdjuster = this.accountService.isAdjuster() || this.accountService.isClaimsManager() || this.accountService.isAssistantClaimsManager();
    this.isAuditor = this.accountService.isGeneralAuditor() || this.accountService.isInsurerAuditor();
  }

  ngAfterViewInit(): void {
    super.build("WarrantyDocumentsComponent", "warranty-documents-component");
    this.refreshDocuments();
  }

  refreshDocuments() {
    this.warrantyDocumentsTable.refresh(
      this.warrantyDocumentService.retrieveAll(this.warrantyId).pipe<IDocument[]>(map(doc => doc.map(x=> {
        x.createdOn = `${moment(x.createdDate).format("YYYY-MM-DD h:mm A (MMMM DD)")}`
        x.documentTypeImage = this.claimsService.getDocumentTypeUrl(x.fileType)
        return x;
      },
      error => {
        this.toastr.error(environment.messages.apiError, "Unable to retrieve warranty documents");
      })))
    );
  }

  addDocument() {
    let dialogRef = this.dialog.open(DocumentAddDialogComponent, {
      panelClass: "warranty-add-dialogs",
      width: "600px",
      data: {
        originId: this.warrantyId,
      },
    });

    dialogRef.componentInstance.onSubmit
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(dialogRes => {
        if (dialogRes.data) {
          dialogRes.data.originId = this.warrantyId;
          dialogRes.data.type = "Action";

          this.blockUI.start();
          this.warrantyDocumentService.add(dialogRes.data)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(res => {
              this.blockUI.stop();
              dialogRef.close();

              if (res.conflicts && res.conflicts.length > 0) {
                this.promptDocumentConflicts(res.conflicts);
              }

              this.toastr.success(
                "Document successfully added. ",
                "Create Document"
              );

              this.refreshDocuments();
            },
            error => {
              this.toastr.error(environment.messages.apiError, "Unable to add document");
            });
        }
      });
  }

  promptDocumentConflicts(documents: IDocument[]) {
    this.dialog.open(DocumentConflictDialogComponent, {
      panelClass: "document-conflicts-dialog",
      autoFocus: true,
      data: documents 
    });
  }

  updateDocument(data) {
    if (data.action == "download") {
      this.downloadDocument(this.warrantyId, data.element.id, data.element.isTerms);
    } else if (data.action == "remove") {
      this.removeDocument(data.element.id);
    } else if (data.action == "move") {
      this.moveDocument(data.element.id);
    }
  }

  downloadDocument(warrantyId: number, documentId: number, isTerms: boolean) {
    if (isTerms) {
      this.warrantyDocumentService
      .downloadTerms(warrantyId, documentId)
      .subscribe((res: HttpResponse<Blob>) => {
        let contentDispositionHeader = res.headers.get("content-disposition");
        // the second part of content-disposition contains the file name
        let fileName = contentDispositionHeader
          .split(";")[1]
          .trim()
          .split("=")[1]
          .replace(/"/g, "");
        FileSaver.saveAs(res.body, fileName);
      },
      error => {
        this.toastr.error(environment.messages.apiError, "Unable to download document");
      });
    }
    else {
      this.warrantyDocumentService
      .download(warrantyId, documentId)
      .subscribe((res: HttpResponse<Blob>) => {
        let contentDispositionHeader = res.headers.get("content-disposition");
        // the second part of content-disposition contains the file name
        let fileName = contentDispositionHeader
          .split(";")[1]
          .trim()
          .split("=")[1]
          .replace(/"/g, "");
        FileSaver.saveAs(res.body, fileName);
      },
      error => {
        this.toastr.error(environment.messages.apiError, "Unable to download document");
      });
    }
    
  }

  removeDocument(documentId: number) {
    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      panelClass: 'part-dialog',
      width: '400px',
      autoFocus: true,
      data: {
        message: "Are you sure you want to remove document?",
        type: DialogType[0].toString()
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.warrantyDocumentService.remove(documentId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(() => {
          this.toastr.success(
            "Document successfully removed. ",
            "Remove Document"
          );
          this.refreshDocuments();
        },
        error => {
          this.toastr.error(environment.messages.apiError, "Unable to remove document");
        });
      }
    });
  }

  moveDocument(documentId: number) {
    const claimRet = new ClaimsRetrieve();
    claimRet.warrantyId = this.warrantyId;
    const claims$ = this.claimsService.retrieveAll(claimRet);

    this.blockUI.start();

    claims$
      .pipe(takeUntil(this.ngUnsubscribe))
      .pipe(finalize(() => this.blockUI.stop()))
      .subscribe(claims => {
        this.dialog.open(DocumentMoveDialogComponent, {
          panelClass: 'document-dialog',
          autoFocus: true,
          data: {
            documentId: documentId,
            documentTypeMap: new Map([
              [[DocumentTypes.Claim], claims.map(item => item.id)],
              [[DocumentTypes.Customer], [this.customerId]]
            ])
          }
        })
        .afterClosed().subscribe(result => {
          if (result) {
            this.refreshDocuments();
          }
        });
    });
  } 

  viewDocument(event){
    let response = event.data;
    if (response) {
      if (response.isTerms) {
        this.warrantyDocumentService
        .downloadTerms(response.warrantyId, response.id)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((res: HttpResponse<Blob>) => {
          this.documentService.openBlob(res, response.fileName);
        },
        error => {
          this.toastr.error(environment.messages.apiError, "Unable to download document");
        });
      }
      else {
        this.warrantyDocumentService
        .download(response.warrantyId, response.id)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((res: HttpResponse<Blob>) => {
          this.documentService.openBlob(res, response.fileName);
        },
        error => {
          this.toastr.error(environment.messages.apiError, "Unable to download document");
        });
      }
      
    }
  }

  public get isClaimClosed(): boolean {
    return this.claimsService.getSelectedClaim?.IsClaimClosed
  }

  public get canEditClosedClaim(): boolean {
    return this.accountService.isClaimsManager();
  }

  public get canInteract(): boolean {
    return this.isAuditor || this.isAdjuster || this.canEditClosedClaim || this.accountService.isDealerServices();
  }

  canRenderActions = (actions: CellActions): boolean => {
    if ((actions & CellActions.Remove) === CellActions.Remove
      || (actions & CellActions.Download) === CellActions.Download
      || (actions & CellActions.Move) === CellActions.Move) {
      return this.isClaimClosed ? this.canEditClosedClaim : (this.isAdjuster || this.canEditClosedClaim || this.accountService.isDealerServices());
    }
    return false;
  }
  
  ngOnDestroy(): any {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }
  
}
